import React, { useEffect, useRef, useState } from "react";

// Modules
import { useSelector } from "react-redux";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  useSortable,
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToHorizontalAxis,
  restrictToWindowEdges,
} from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";

// App
import { serverUrl } from "../../config";
import MediaLibrary from "../../partials/mediaLibrary";
import CategorySelector from "../../partials/categorySelector";
import EntitySelector from "../training/entitySelector";
import MilestoneBuilder from "./milestoneBuilder";

// UI components
import { Col, Row } from "react-bootstrap";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import ImageIcon from "@mui/icons-material/ImageOutlined";
import MovieIcon from "@mui/icons-material/MovieOutlined";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import SendLinkIcon from "@mui/icons-material/ForwardToInboxOutlined";
import SlideshowIcon from "@mui/icons-material/SlideshowOutlined";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import LinkIcon from "@mui/icons-material/LinkOutlined";
import MicIcon from "@mui/icons-material/MicNoneRounded";
import CollectionsIcon from "@mui/icons-material/CollectionsOutlined";
import ArticleIcon from "@mui/icons-material/ArticleOutlined";
import EventIcon from "@mui/icons-material/CalendarTodayOutlined";

// Content types a buyer can consume anonymously - keep in sync with the dsr
// module's SalesRoom::referenceFieldSettings().
export const ROOM_CONTENT_TYPES = [
  "presentation",
  "document",
  "video",
  "link",
  "podcast",
  "gallery",
  "news",
  "event",
];

// Icon + human label per content type, mirroring buyerRoom.js so the seller's
// cards read exactly like the buyer's.
const typeMeta = {
  presentation: { icon: SlideshowIcon, label: "Presentation" },
  document: { icon: DescriptionIcon, label: "Document" },
  video: { icon: MovieIcon, label: "Video" },
  link: { icon: LinkIcon, label: "Link" },
  podcast: { icon: MicIcon, label: "Podcast" },
  gallery: { icon: CollectionsIcon, label: "Gallery" },
  news: { icon: ArticleIcon, label: "News" },
  event: { icon: EventIcon, label: "Event" },
};

const ROOM_STATUSES = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "closed", label: "Closed" },
];

const ALERT_MODES = [
  { value: "off", label: "Off" },
  { value: "first_open", label: "First visit only" },
  { value: "every_open", label: "Every visit" },
];

const ACCESS_MODES = [
  { value: "link", label: "Anyone with the link" },
  { value: "password", label: "Anyone with the link + passcode" },
  { value: "restricted", label: "Invited people only" },
];

const newSectionId = () =>
  `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const createSection = () => ({
  id: newSectionId(),
  title: "",
  description: "",
  items: [],
});

const itemKey = (item) => `${item.type || item.bundle}:${item.id}`;

const thumbUrl = (thumbnail) => {
  if (!thumbnail) {
    return null;
  }
  return thumbnail.indexOf("http") === 0 ? thumbnail : `${serverUrl}${thumbnail}`;
};

const logoUrl = (logo) => {
  if (!logo) {
    return null;
  }
  return logo.indexOf("http") === 0 ? logo : `${serverUrl}${logo}`;
};

/**
 * A borderless textarea that grows to fit its content instead of scrolling,
 * so the hero expands exactly like the buyer's (which has no fixed height).
 */
function AutoGrowTextarea({ value, ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [value]);
  return <textarea ref={ref} value={value} {...rest} />;
}

/**
 * A content card that can be dragged to reorder within its section.
 */
function SortableCard({ item, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: itemKey(item) });
  const meta = typeMeta[item.type] || typeMeta.document;
  const TypeIcon = meta.icon;
  const src = thumbUrl(item.thumbnail);
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="col-12 col-sm-6 col-lg-3 sales-room-canvas__card-col"
    >
      {/* The whole card is the drag handle; the remove button stops the
          pointer so a click there never starts a drag. */}
      <div
        className="buyer-room__card sales-room-canvas__card"
        {...attributes}
        {...listeners}
      >
        <div className="sales-room-canvas__card-tools">
          <Tooltip title="Remove from section">
            <IconButton
              size="small"
              onClick={onRemove}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
        <div className="buyer-room__card-thumbnail">
          {src ? (
            <img src={src} alt={item.title} />
          ) : (
            <TypeIcon className="buyer-room__card-icon" />
          )}
          <span className="buyer-room__card-type-chip">{meta.label}</span>
        </div>
        <div className="buyer-room__card-body">
          <Typography
            owner="admin"
            variant="body1"
            className="buyer-room__card-title"
          >
            {item.title}
          </Typography>
          <Typography
            owner="admin"
            variant="body2"
            className="buyer-room__card-summary"
          >
            {item.summary}
          </Typography>
        </div>
      </div>
    </div>
  );
}

/**
 * A section tab that can be dragged to reorder sections. A plain click still
 * switches to the tab (drag only starts past the 5px activation distance).
 */
function SortableTab({ id, label, active, onSelect }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Tooltip title="Drag to reorder, click to open">
      <button
        ref={setNodeRef}
        style={style}
        type="button"
        role="tab"
        aria-selected={active}
        className={`buyer-room__tab sales-room-canvas__section-tab ${
          active ? "buyer-room__tab--active" : ""
        }`}
        onClick={onSelect}
        {...attributes}
        {...listeners}
      >
        {label}
      </button>
    </Tooltip>
  );
}

/**
 * Visual, buyer-styled editor for a sales room. Renders the same hero /
 * tabbed-sections / content-card layout the buyer sees (reusing the
 * `buyer-room__*` classes + the `--dsr-accent` variable), but every element is
 * editable in place: type into the hero, drag content cards / section tabs to
 * reorder, rename sections. Non-visual room settings (status, access, alerts,
 * contacts) live in the Settings dialog, opened from the edit-screen toolbar.
 *
 * It reads and writes the exact same `form` shape SalesRoomEdit builds, so
 * buildRoomPayload()/sectionsFromRoom()/milestonesFromRoom() are unchanged.
 */
export default function SalesRoomCanvas(props) {
  const { form, setForm, onSendAccessLink, showSettings, setShowSettings } =
    props;

  const user = useSelector((state) => state).authReducer.user;
  const permissions = useSelector((state) => state).permissionsReducer
    .permissions;
  const canManageTemplates = permissions?.manage_sales_room_templates === true;

  const [activeTabKey, setActiveTabKey] = useState("dashboard");
  // Which media picker is open: 'logo' | 'video' | null.
  const [mediaPicker, setMediaPicker] = useState(null);
  // Content picker: the section id it will add to, or null when closed.
  const [selectorSectionId, setSelectorSectionId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const accent = form.colour || "#0e6ba8";

  const setField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  // --- Section + item operations -------------------------------------------
  const setSections = (sections) => setField("sections", sections);

  const updateSection = (id, changes) =>
    setSections(
      form.sections.map((section) =>
        section.id === id ? { ...section, ...changes } : section
      )
    );

  const addSection = () => {
    const section = createSection();
    setSections([...form.sections, section]);
    setActiveTabKey(`section-${section.id}`);
  };

  const removeSection = (id) => {
    setSections(form.sections.filter((section) => section.id !== id));
    setActiveTabKey("dashboard");
  };

  const removeItem = (section, index) =>
    updateSection(section.id, {
      items: section.items.filter((_, i) => i !== index),
    });

  const handleSectionDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = form.sections.findIndex((s) => s.id === active.id);
    const newIndex = form.sections.findIndex((s) => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }
    setSections(arrayMove(form.sections, oldIndex, newIndex));
  };

  const handleCardDragEnd = (section) => (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = section.items.findIndex((i) => itemKey(i) === active.id);
    const newIndex = section.items.findIndex((i) => itemKey(i) === over.id);
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }
    updateSection(section.id, {
      items: arrayMove(section.items, oldIndex, newIndex),
    });
  };

  // --- Contacts (settings dialog) ------------------------------------------
  const updateContact = (index, field, value) =>
    setField(
      "contacts",
      form.contacts.map((contact, i) =>
        i === index ? { ...contact, [field]: value } : contact
      )
    );

  // --- Derived --------------------------------------------------------------
  const sections = form.sections || [];
  const tabs = [
    { key: "dashboard", label: "Dashboard" },
    ...sections.map((section, index) => ({
      key: `section-${section.id}`,
      label: section.title || `Section ${index + 1}`,
    })),
    { key: "milestones", label: "Milestones" },
  ];
  const activeTab = tabs.find((tab) => tab.key === activeTabKey) || tabs[0];
  const activeSection = sections.find(
    (section) => `section-${section.id}` === activeTab.key
  );
  const selectorSection = sections.find(
    (section) => section.id === selectorSectionId
  );

  const doneMilestones = (form.milestones || []).filter(
    (milestone) => milestone.status === "done"
  ).length;
  const totalMilestones = (form.milestones || []).length;

  return (
    <div className="sales-room-canvas" style={{ "--dsr-accent": accent }}>
      {/* Tab bar: Dashboard, one draggable tab per section, Milestones, add. */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleSectionDragEnd}
        modifiers={[restrictToHorizontalAxis, restrictToWindowEdges]}
      >
        <div className="buyer-room__tabs sales-room-canvas__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab.key === "dashboard"}
            className={`buyer-room__tab ${
              activeTab.key === "dashboard" ? "buyer-room__tab--active" : ""
            }`}
            onClick={() => setActiveTabKey("dashboard")}
          >
            Dashboard
          </button>
          <SortableContext
            items={sections.map((section) => section.id)}
            strategy={horizontalListSortingStrategy}
          >
            {sections.map((section, index) => (
              <SortableTab
                key={section.id}
                id={section.id}
                label={section.title || `Section ${index + 1}`}
                active={activeTab.key === `section-${section.id}`}
                onSelect={() => setActiveTabKey(`section-${section.id}`)}
              />
            ))}
          </SortableContext>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab.key === "milestones"}
            className={`buyer-room__tab ${
              activeTab.key === "milestones" ? "buyer-room__tab--active" : ""
            }`}
            onClick={() => setActiveTabKey("milestones")}
          >
            Milestones
          </button>
          <Tooltip title="Add a section">
            <button
              type="button"
              className="sales-room-canvas__tab-add"
              onClick={addSection}
            >
              <AddIcon fontSize="small" /> Section
            </button>
          </Tooltip>
        </div>
      </DndContext>

      {/* ---- Dashboard tab: the branded hero, edited in place ------------- */}
      {activeTab.key === "dashboard" && (
        <section className="buyer-room__hero sales-room-canvas__hero mb-4">
          <div className="sales-room-canvas__hero-toolbar">
            <Tooltip title="Accent colour">
              <label className="sales-room-canvas__accent">
                <input
                  type="color"
                  value={accent}
                  onChange={(e) => setField("colour", e.target.value)}
                />
              </label>
            </Tooltip>
          </div>
          <Row className="align-items-center">
            <Col xs={12} lg={7}>
              <div className="buyer-room__hero-inner">
                {form.logoUrl ? (
                  <div className="buyer-room__logo-chip sales-room-canvas__logo">
                    <img src={logoUrl(form.logoUrl)} alt="Room logo" />
                    <div className="sales-room-canvas__logo-actions">
                      <Tooltip title="Change logo">
                        <IconButton
                          size="small"
                          onClick={() => setMediaPicker("logo")}
                        >
                          <ImageIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove logo">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setField("logoFid", null);
                            setField("logoUrl", null);
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                ) : (
                  <Tooltip title="Add a logo">
                    <button
                      type="button"
                      className="sales-room-canvas__logo-add"
                      onClick={() => setMediaPicker("logo")}
                    >
                      <ImageIcon fontSize="small" /> Add logo
                    </button>
                  </Tooltip>
                )}

                <input
                  className="sales-room-canvas__hero-field sales-room-canvas__title-input"
                  value={form.title}
                  placeholder="Room title"
                  onChange={(e) => setField("title", e.target.value)}
                />

                <div className="sales-room-canvas__company-line">
                  <span>Prepared for</span>
                  <input
                    className="sales-room-canvas__hero-field sales-room-canvas__company-input"
                    value={form.company}
                    placeholder="company name"
                    onChange={(e) => setField("company", e.target.value)}
                  />
                </div>

                <AutoGrowTextarea
                  className="sales-room-canvas__hero-field sales-room-canvas__lead-input"
                  value={form.description}
                  placeholder="Add a short welcome message for the buyer..."
                  onChange={(e) => setField("description", e.target.value)}
                />
              </div>
            </Col>
            <Col xs={12} lg={5}>
              <div className="buyer-room__intro-card sales-room-canvas__intro-card">
                {form.introVideoMid ? (
                  <>
                    <div className="buyer-room__intro-video sales-room-canvas__intro-video">
                      <div className="sales-room-canvas__intro-placeholder">
                        <MovieIcon />
                        <Typography owner="admin" variant="body2">
                          {form.introVideoName || `Video ${form.introVideoMid}`}
                        </Typography>
                      </div>
                    </div>
                    <div className="sales-room-canvas__intro-actions">
                      <Tooltip title="Change intro video">
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ textTransform: "none" }}
                          onClick={() => setMediaPicker("video")}
                        >
                          Change
                        </Button>
                      </Tooltip>
                      <Tooltip title="Remove intro video">
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ textTransform: "none" }}
                          onClick={() => {
                            setField("introVideoMid", null);
                            setField("introVideoName", null);
                          }}
                        >
                          Remove
                        </Button>
                      </Tooltip>
                    </div>
                  </>
                ) : (
                  <Tooltip title="Add an intro video">
                    <button
                      type="button"
                      className="sales-room-canvas__intro-add"
                      onClick={() => setMediaPicker("video")}
                    >
                      <MovieIcon />
                      <Typography owner="admin" variant="body2">
                        Add an intro video
                      </Typography>
                      <Typography owner="admin" variant="caption">
                        A short personal introduction shown at the top of the
                        buyer's room.
                      </Typography>
                    </button>
                  </Tooltip>
                )}
                <input
                  className="sales-room-canvas__intro-caption"
                  value={form.introText}
                  placeholder="Intro caption (optional)"
                  maxLength={255}
                  onChange={(e) => setField("introText", e.target.value)}
                />
              </div>
            </Col>
          </Row>
        </section>
      )}

      {/* ---- Section tab: editable header + draggable content cards ------- */}
      {activeSection && (
        <section className="sales-room-canvas__section mb-4">
          <div className="sales-room-canvas__section-head">
            <div className="sales-room-canvas__section-headings">
              <input
                className="sales-room-canvas__section-title-input"
                value={activeSection.title}
                placeholder="Section title"
                onChange={(e) =>
                  updateSection(activeSection.id, { title: e.target.value })
                }
              />
              <input
                className="sales-room-canvas__section-desc-input"
                value={activeSection.description}
                placeholder="Section description (optional)"
                onChange={(e) =>
                  updateSection(activeSection.id, {
                    description: e.target.value,
                  })
                }
              />
            </div>
            <div className="sales-room-canvas__section-tools">
              <Tooltip title="Delete section">
                <IconButton
                  size="small"
                  onClick={() => removeSection(activeSection.id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleCardDragEnd(activeSection)}
            modifiers={[restrictToWindowEdges]}
          >
            <SortableContext
              items={activeSection.items.map(itemKey)}
              strategy={rectSortingStrategy}
            >
              <Row className="gy-3">
                {activeSection.items.map((item, index) => (
                  <SortableCard
                    key={itemKey(item)}
                    item={item}
                    onRemove={() => removeItem(activeSection, index)}
                  />
                ))}
                <div className="col-12 col-sm-6 col-lg-3">
                  <Tooltip title="Add content to this section">
                    <button
                      type="button"
                      className="sales-room-canvas__add-card"
                      onClick={() => setSelectorSectionId(activeSection.id)}
                    >
                      <AddIcon />
                      <Typography owner="admin" variant="body2">
                        Add content
                      </Typography>
                    </button>
                  </Tooltip>
                </div>
              </Row>
            </SortableContext>
          </DndContext>
        </section>
      )}

      {/* ---- Milestones tab: live buyer progress bar + row editor -------- */}
      {activeTab.key === "milestones" && (
        <section className="sales-room-canvas__milestones mb-4">
          <Typography
            owner="admin"
            variant="body2"
            className="mb-2 sales-room-canvas__hint"
          >
            Milestones appear in the buyer's room with this progress bar. Buyers
            can tick off the steps you assign to them.
          </Typography>
          <div className="buyer-room__progress">
            <div
              className="buyer-room__progress-bar"
              style={{
                width: `${
                  totalMilestones
                    ? Math.round((doneMilestones / totalMilestones) * 100)
                    : 0
                }%`,
              }}
            />
          </div>
          <Typography
            owner="admin"
            variant="caption"
            className="buyer-room__progress-label d-block mb-3"
          >
            {doneMilestones} of {totalMilestones} steps complete
          </Typography>
          <MilestoneBuilder
            milestones={form.milestones}
            setMilestones={(milestones) => setField("milestones", milestones)}
          />
        </section>
      )}

      {/* ---- Content picker ---------------------------------------------- */}
      {selectorSectionId && (
        <EntitySelector
          showSelector={!!selectorSectionId}
          setShowSelector={(open) => {
            if (!open) {
              setSelectorSectionId(null);
            }
          }}
          allowedTypes={ROOM_CONTENT_TYPES}
          currentBlock={{ data: { entities: selectorSection?.items || [] } }}
          insertSelections={(selection) => {
            if (selectorSectionId) {
              updateSection(selectorSectionId, { items: selection });
            }
          }}
        />
      )}

      {/* ---- Media pickers (logo / intro video) -------------------------- */}
      {mediaPicker && (
        <MediaLibrary
          user={user}
          csrf_token={user.csrf_token}
          mediaType={mediaPicker === "logo" ? "image" : "video"}
          files={{ aspectRatio: null }}
          showMediaLibrary={!!mediaPicker}
          setShowMediaLibrary={(open) => {
            if (!open) {
              setMediaPicker(null);
            }
          }}
          onMediaInsert={(media) => {
            if (mediaPicker === "logo") {
              setForm((prev) => ({
                ...prev,
                logoFid: media.fid,
                logoUrl: media.field_media_image || prev.logoUrl,
              }));
            } else {
              setForm((prev) => ({
                ...prev,
                introVideoMid: media.mid || media.fid,
                introVideoName: media.name || media.filename || null,
              }));
            }
            setMediaPicker(null);
          }}
        />
      )}

      {/* ---- Settings dialog (non-visual room options) ------------------- */}
      <Dialog
        open={!!showSettings}
        onClose={() => setShowSettings(false)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        className="sales-room-canvas__settings"
      >
        <div className="sales-room-canvas__settings-head">
          <Typography owner="admin" variant="h6">
            Room settings
          </Typography>
          <Tooltip title="Close">
            <IconButton size="small" onClick={() => setShowSettings(false)}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </div>

        <DialogContent dividers className="sales-room-canvas__settings-body">
          <div className="form-group">
            <label>Status</label>
            <CategorySelector
              name="roomStatus"
              label="Status"
              categories={ROOM_STATUSES}
              selectedCategory={ROOM_STATUSES.find(
                (status) => status.value === form.status
              )}
              outlined
              variant="outlined"
              size="small"
              handleCategorySelection={(value) => setField("status", value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="room-expiry">Expires</label>
            <TextField
              id="room-expiry"
              className="date-input-field w-100"
              type="datetime-local"
              variant="outlined"
              size="small"
              value={form.expiry}
              onChange={(e) => setField("expiry", e.target.value)}
              helperText="Leave empty for no expiry"
            />
          </div>

          <div className="form-group">
            <label>Link access</label>
            <CategorySelector
              name="roomAccess"
              label="Link access"
              categories={ACCESS_MODES}
              selectedCategory={ACCESS_MODES.find(
                (mode) => mode.value === (form.accessMode || "link")
              )}
              outlined
              variant="outlined"
              size="small"
              handleCategorySelection={(value) => setField("accessMode", value)}
            />
          </div>

          {form.accessMode === "password" && (
            <div className="form-group">
              <label htmlFor="room-password">Room passcode</label>
              <TextField
                id="room-password"
                type={showPassword ? "text" : "password"}
                variant="outlined"
                fullWidth
                size="small"
                autoComplete="new-password"
                value={form.password || ""}
                placeholder="No passcode"
                helperText="Share it with your buyer separately from the room link."
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={showPassword ? "Hide passcode" : "Show passcode"}
                      >
                        <IconButton
                          size="small"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    password: e.target.value,
                    passwordDirty: true,
                  }))
                }
              />
            </div>
          )}

          {form.accessMode === "restricted" && (
            <Typography owner="admin" variant="body2" className="mb-2">
              Buyers verify their email with a secure link - only people listed
              under Buyer contacts below have access.
            </Typography>
          )}

          <div className="form-group">
            <label>Visit alerts</label>
            <CategorySelector
              name="roomAlerts"
              label="Visit alerts"
              categories={ALERT_MODES}
              selectedCategory={ALERT_MODES.find(
                (mode) => mode.value === form.alertMode
              )}
              outlined
              variant="outlined"
              size="small"
              handleCategorySelection={(value) => setField("alertMode", value)}
            />
            <FormControlLabel
              className="mt-1"
              control={
                <Checkbox
                  size="small"
                  checked={!!form.dailyDigest}
                  onChange={(e) => setField("dailyDigest", e.target.checked)}
                />
              }
              label={
                <Typography owner="admin" variant="body2">
                  Daily digest email (only on days with activity)
                </Typography>
              }
            />
          </div>

          {canManageTemplates && (
            <div className="form-group">
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!!form.isTemplate}
                    onChange={(e) => setField("isTemplate", e.target.checked)}
                  />
                }
                label={
                  <Typography owner="admin" variant="body2">
                    Save as template (visible to all sellers)
                  </Typography>
                }
              />
            </div>
          )}

          <div className="sales-room-canvas__settings-section">
            <Typography owner="admin" variant="h6" className="ss-subtitle mb-2">
              {form.accessMode === "restricted"
                ? "Buyer contacts (access list)"
                : "Buyer contacts"}
            </Typography>
            {form.contacts.map((contact, index) => (
              <div
                className="sales-room-canvas__contact"
                key={`contact-${index}`}
              >
                <TextField
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={contact.name}
                  placeholder="Name"
                  onInput={(e) => updateContact(index, "name", e.target.value)}
                />
                <TextField
                  variant="outlined"
                  fullWidth
                  size="small"
                  type="email"
                  value={contact.email}
                  placeholder="Email"
                  onInput={(e) => updateContact(index, "email", e.target.value)}
                />
                <TextField
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={contact.role}
                  placeholder="Role"
                  onInput={(e) => updateContact(index, "role", e.target.value)}
                />
                <div className="sales-room-canvas__contact-actions">
                  {form.accessMode === "restricted" &&
                    onSendAccessLink &&
                    contact.email && (
                      <Tooltip title="Send access link to this contact">
                        <IconButton
                          size="small"
                          onClick={() => onSendAccessLink(contact.email)}
                        >
                          <SendLinkIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  <Tooltip title="Remove contact">
                    <IconButton
                      size="small"
                      onClick={() =>
                        setField(
                          "contacts",
                          form.contacts.filter((_, i) => i !== index)
                        )
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            ))}
            <Button
              variant="outlined"
              size="small"
              sx={{ textTransform: "none" }}
              startIcon={<AddIcon />}
              onClick={() =>
                setField("contacts", [
                  ...form.contacts,
                  { name: "", email: "", role: "" },
                ])
              }
            >
              Add contact
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
